import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarPlus, Check, Clock3, Loader2, Plane, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Textarea } from "../ui/textarea";
import { api, errMsg, fmtDay, fmtDateTime, istDateISO } from "../../lib/api";
import { TableRowsSkeleton } from "../PaginationBar";
import { StaffLabel } from "../StaffAvatar";
import { ReasonDialog } from "./ReasonDialog";
import { LeaveStatusBadge, StatusBadge } from "./shared";

const LEAVE_TYPES = [["casual", "Casual leave"], ["sick", "Sick leave"], ["earned", "Earned leave"], ["other", "Other"]];

/** Phase 168 — staff-side "Request leave" dialog (collectors + supervisors). */
export function LeaveRequestDialog({ code, open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ date: istDateISO(), leave_type: "casual", reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ date: istDateISO(), leave_type: "casual", reason: "" }); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/attendance/leave-requests`, form);
      toast.success(`Leave requested for ${fmtDay(form.date)}`, { description: "Your admin has been notified and will approve or reject it." });
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="leave-request-dialog">
        <form onSubmit={submit} className="space-y-3">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Plane className="h-5 w-5 text-primary" aria-hidden="true" /> Request leave</DialogTitle>
            <DialogDescription>Pick the date, leave type and reason. The admin decides; you are notified either way. If the day passes undecided it is recorded as Absent until the admin acts.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} required className="h-10" data-testid="leave-date-input" /></div>
            <div className="space-y-1">
              <Label className="text-xs">Leave type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm((f) => ({ ...f, leave_type: v }))}>
                <SelectTrigger className="h-10" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`leave-type-option-${k}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Reason (required)</Label>
            <Textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} rows={3} maxLength={500} required placeholder="Why do you need the day off?" data-testid="leave-reason-input" />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="leave-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy || form.reason.trim().length < 3 || !form.date} data-testid="leave-submit-button">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Submit request</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Staff-side list of own leave requests with a Request button + withdraw for pending ones. */
export function MyLeaveRequests({ code, items, onChanged, compact = false }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const cancel = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/t/${code}/attendance/leave-requests/${r.id}/cancel`);
      toast.success(`Leave request for ${fmtDay(r.date)} withdrawn`);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyId(null); }
  };
  return (
    <Card className="shadow-sm" data-testid="my-leave-requests">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium flex items-center gap-2"><Plane className="h-4 w-4 text-primary" aria-hidden="true" /> My leave requests
            {items?.some((r) => r.status === "pending") && <Badge variant="secondary" className="text-[10px]" data-testid="my-leave-pending-count">{items.filter((r) => r.status === "pending").length} pending</Badge>}</p>
          <Button size="sm" className="h-9" onClick={() => setOpen(true)} data-testid="request-leave-button"><CalendarPlus className="h-4 w-4 mr-1.5" /> Request leave</Button>
        </div>
        {!items ? <TableRowsSkeleton rows={2} cols={4} testId="my-leave-skeleton" /> : items.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center" data-testid="my-leave-empty">No leave requests yet. Use “Request leave” to ask for a day off.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {items.slice(0, compact ? 5 : 100).map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm" data-testid={`my-leave-row-${r.id}`} data-status={r.status}>
                <span className="font-medium whitespace-nowrap">{fmtDay(r.date)}</span>
                <span className="text-xs text-muted-foreground">{r.leave_type_label}</span>
                <LeaveStatusBadge status={r.status} testId={`my-leave-status-${r.id}`} />
                <span className="text-xs text-muted-foreground truncate max-w-full sm:max-w-[260px]" title={r.reason}>{r.reason}</span>
                {r.decision_reason && <span className="text-xs w-full sm:w-auto"><span className="text-muted-foreground">{r.decided_by_name}:</span> {r.decision_reason}{r.retroactive ? " (after the day)" : ""}</span>}
                {r.status === "pending" && r.auto_absent_at && <span className="text-[11px] text-[hsl(var(--warning))]">Day passed — recorded Absent until decided</span>}
                {r.status === "pending" && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" disabled={busyId === r.id} onClick={() => cancel(r)} data-testid={`my-leave-cancel-${r.id}`}>
                    {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><X className="h-3.5 w-3.5 mr-1" /> Withdraw</>}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <LeaveRequestDialog code={code} open={open} onOpenChange={setOpen} onCreated={onChanged} />
    </Card>
  );
}

/** Admin tab: every request, pending first; approve / reject with a mandatory reason (late decisions update attendance). */
export function LeaveRequestsTab({ code, onChanged }) {
  const [status, setStatus] = useState("pending");
  const [month, setMonth] = useState("");
  const [role, setRole] = useState("all");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null); // {req, approve}
  const today = istDateISO();

  const load = useCallback(() => {
    api.get(`/t/${code}/attendance/leave-requests`, { params: { status: status === "all" ? undefined : status, month: month || undefined, role: role === "all" ? undefined : role } })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(errMsg(e)));
  }, [code, status, month, role]);
  useEffect(() => { load(); }, [load]);

  const decide = async (reason) => {
    const { req, approve } = decision;
    try {
      const r = await api.post(`/t/${code}/attendance/leave-requests/${req.id}/decide`, { approve, reason });
      const d = r.data;
      toast.success(`Leave ${approve ? "approved" : "rejected"} for ${req.requester_name} — ${fmtDay(req.date)}`, {
        description: d.retroactive
          ? (d.attendance ? `Decided after the day: attendance for ${fmtDay(req.date)} is now ${d.attendance.status_label}.` : "Decided after the day — logged as retroactive.")
          : "The requester has been notified.",
      });
      setDecision(null);
      load();
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const items = data?.items || [];
  const months = useMemo(() => {
    const out = [];
    const d = new Date();
    for (let i = -2; i <= 3; i++) { const x = new Date(d.getFullYear(), d.getMonth() + i, 1); out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`); }
    return out;
  }, []);

  return (
    <div className="space-y-3" data-testid="leave-requests-tab">
      <Card className="shadow-sm">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="leave-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["pending", "approved", "rejected", "cancelled", "all"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Month</Label>
            <Select value={month || "any"} onValueChange={(v) => setMonth(v === "any" ? "" : v)}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="leave-filter-month"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any month</SelectItem>{months.map((m) => <SelectItem key={m} value={m}>{new Date(Number(m.slice(0, 4)), Number(m.slice(5)) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Requested by</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="leave-filter-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Everyone</SelectItem><SelectItem value="tax_collector">Tax collectors</SelectItem><SelectItem value="supervisor">Supervisors</SelectItem></SelectContent>
            </Select>
          </div>
          {data && <span className="text-xs text-muted-foreground ml-auto" data-testid="leave-count">{items.length} {items.length === 1 ? "request" : "requests"}{data.pending ? ` · ${data.pending} pending` : ""}</span>}
        </CardContent>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {error ? <p className="p-4 text-sm text-destructive" data-testid="leave-error">{error}</p> : !data ? <TableRowsSkeleton rows={4} cols={6} testId="leave-skeleton" /> : items.length === 0 ? (
            <p className="px-4 py-10 text-sm text-muted-foreground text-center" data-testid="leave-empty">{status === "pending" ? "No pending leave requests — you are all caught up." : "No leave requests match these filters."}</p>
          ) : (
            <Table data-testid="leave-table">
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">Requested by</TableHead><TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Reason</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs hidden lg:table-cell">Decision</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((r) => {
                  const passed = r.date < today;
                  return (
                    <TableRow key={r.id} data-testid={`leave-row-${r.id}`} data-status={r.status}>
                      <TableCell className="py-2 whitespace-nowrap">{fmtDay(r.date)}{passed && r.status === "pending" && <span className="block text-[10px] text-[hsl(var(--warning))]"><Clock3 className="h-2.5 w-2.5 inline mr-0.5" />Day passed — Absent until decided</span>}</TableCell>
                      <TableCell className="py-2"><StaffLabel code={code} userId={r.requester_id} name={r.requester_name} empCode={r.employee_id} /><span className="block text-[10px] text-muted-foreground">{r.requester_role === "supervisor" ? "Supervisor" : "Tax collector"}{r.reports_to_name ? ` · reports to ${r.reports_to_name}` : ""}</span></TableCell>
                      <TableCell className="py-2 text-xs">{r.leave_type_label}</TableCell>
                      <TableCell className="py-2 text-xs text-muted-foreground max-w-[260px]"><span className="line-clamp-2" title={r.reason}>{r.reason}</span><span className="block text-[10px]">Requested {fmtDateTime(r.created_at)}</span></TableCell>
                      <TableCell className="py-2"><LeaveStatusBadge status={r.status} testId={`leave-status-${r.id}`} />{r.retroactive && <Badge variant="outline" className="ml-1 text-[10px]" data-testid={`leave-retro-${r.id}`}>Retroactive</Badge>}</TableCell>
                      <TableCell className="py-2 hidden lg:table-cell text-xs text-muted-foreground max-w-[240px]">
                        {r.decided_by_name ? <><span className="text-foreground">{r.decided_by_name}</span> · {fmtDateTime(r.decided_at)}<span className="block line-clamp-2" title={r.decision_reason}>{r.decision_reason}</span></> : "—"}
                      </TableCell>
                      <TableCell className="py-2 text-right whitespace-nowrap">
                        {r.status !== "cancelled" && r.status !== "approved" && (
                          <Button size="sm" className="h-8 text-xs mr-1" onClick={() => setDecision({ req: r, approve: true })} data-testid={`leave-approve-${r.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                        )}
                        {r.status !== "cancelled" && r.status !== "rejected" && (
                          <Button size="sm" variant="outline" className="h-8 text-xs text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setDecision({ req: r, approve: false })} data-testid={`leave-reject-${r.id}`}><X className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <ReasonDialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)} icon={decision?.approve ? Check : X} destructive={decision ? !decision.approve : false}
        title={decision ? `${decision.approve ? "Approve" : "Reject"} leave — ${decision.req.requester_name}, ${fmtDay(decision.req.date)}` : ""}
        description={decision ? (decision.req.date < today
          ? (decision.approve ? "The day has already passed: approving changes that day's attendance from Absent to Leave (logged as a retroactive change)."
            : "The day has already passed: rejecting keeps the day as recorded (Absent). The change is logged with your name and the time.")
          : `${decision.req.leave_type_label} — “${decision.req.reason}”. The requester is notified with your reason.`) : ""}
        confirmLabel={decision?.approve ? "Approve leave" : "Reject leave"} onConfirm={decide} testId="leave-decision-dialog">
        {decision?.req?.requester_role === "tax_collector" && <p className="text-xs text-muted-foreground">Attendance on that day: <StatusBadge status={decision.req.date <= today ? (decision.req.status === "approved" ? "leave" : "absent") : "leave"} size="xs" /> {decision.req.date <= today ? "(current)" : "(after approval)"}</p>}
      </ReasonDialog>
    </div>
  );
}
